export interface SubCategory {
  id: string;
  label: string;
  description: string;
  order: number;
}

export interface Cluster {
  id: string;
  label: string;
  description: string;
  image_url?: string;
  order: number;
  subCategories: SubCategory[];
}

export const redistributeSubAllocations = (
  cluster: Cluster,
  clusterValue: number,
  previousSubs: Record<string, number>,
) => {
  const subIds = cluster.subCategories.map((s) => s.id);

  if (subIds.length === 0) {
    return previousSubs;
  }

  const updated = { ...previousSubs };
  const currentTotal = subIds.reduce((sum, id) => sum + (previousSubs[id] || 0), 0);

  if (clusterValue === 0) {
    subIds.forEach((id) => {
      updated[id] = 0;
    });
    return updated;
  }

  if (currentTotal === 0) {
    const even = Math.floor(clusterValue / subIds.length);
    const remainder = clusterValue - even * subIds.length;
    subIds.forEach((id, index) => {
      updated[id] = even + (index < remainder ? 1 : 0);
    });
    return updated;
  }

  let accumulated = 0;
  subIds.forEach((id, index) => {
    const fraction = (previousSubs[id] || 0) / currentTotal;
    const scaled =
      index === subIds.length - 1 ? Math.max(0, clusterValue - accumulated) : Math.round(fraction * clusterValue);
    updated[id] = scaled;
    accumulated += scaled;
  });

  return updated;
};

export const adjustEvenly = (
  allocations: Record<string, number>,
  targets: string[],
  amount: number,
  mode: "add" | "deduct",
): { updated: Record<string, number>; remaining: number } => {
  const updated = { ...allocations };
  let remaining = amount;

  if (remaining <= 0 || targets.length === 0) {
    return { updated, remaining };
  }

  let progressed = true;
  while (remaining > 0 && progressed) {
    progressed = false;
    for (const id of targets) {
      if (remaining <= 0) break;
      if (mode === "deduct") {
        if (updated[id] <= 0) continue;
        updated[id] -= 1;
        remaining -= 1;
        progressed = true;
      } else {
        updated[id] += 1;
        remaining -= 1;
        progressed = true;
      }
    }
  }

  return { updated, remaining };
};

export const applyClusterChange = (
  clusters: Cluster[],
  baseAllocations: Record<string, number>,
  clusterId: string,
  clampedValue: number,
) => {
  const clusterIndex = clusters.findIndex((cluster) => cluster.id === clusterId);
  const below = clusters.slice(clusterIndex + 1).map((cluster) => cluster.id);
  const above = clusters.slice(0, clusterIndex).map((cluster) => cluster.id).reverse();
  const previousValue = baseAllocations[clusterId] || 0;
  const diff = clampedValue - previousValue;

  let updatedClusterAllocations: Record<string, number> = {
    ...baseAllocations,
    [clusterId]: clampedValue,
  };

  if (diff > 0) {
    const { updated: afterBelow, remaining: afterBelowRemaining } = adjustEvenly(
      updatedClusterAllocations,
      below,
      diff,
      "deduct",
    );
    const { updated: afterAbove, remaining } = adjustEvenly(afterBelow, above, afterBelowRemaining, "deduct");
    updatedClusterAllocations = afterAbove;

    if (remaining > 0) {
      updatedClusterAllocations[clusterId] = clampedValue - remaining;
    }
  } else if (diff < 0) {
    const { updated: afterBelow, remaining: afterBelowRemaining } = adjustEvenly(
      updatedClusterAllocations,
      below,
      Math.abs(diff),
      "add",
    );
    const { updated: afterAbove, remaining } = adjustEvenly(afterBelow, above, afterBelowRemaining, "add");
    updatedClusterAllocations = afterAbove;

    if (remaining > 0) {
      updatedClusterAllocations[clusterId] = clampedValue + remaining;
    }
  }

  return updatedClusterAllocations;
};

export const applySubChange = (
  baseAllocations: Record<string, number>,
  subId: string,
  clampedValue: number,
  parentCluster: Cluster,
  clusterValue: number,
) => {
  const subIds = parentCluster.subCategories.map((sub) => sub.id);
  const subIndex = subIds.findIndex((id) => id === subId);
  const belowSubs = subIds.slice(subIndex + 1);
  const aboveSubs = subIds.slice(0, subIndex).reverse();
  const updated = { ...baseAllocations };
  const previousValue = baseAllocations[subId] || 0;
  const diff = clampedValue - previousValue;
  updated[subId] = clampedValue;

  if (clusterValue === 0) {
    subIds.forEach((id) => {
      updated[id] = 0;
    });
    return updated;
  }

  if (subIds.length === 1) {
    updated[subId] = clusterValue;
    return updated;
  }

  if (diff > 0) {
    const { updated: afterBelow, remaining: afterBelowRemaining } = adjustEvenly(updated, belowSubs, diff, "deduct");
    const { updated: afterAbove, remaining } = adjustEvenly(afterBelow, aboveSubs, afterBelowRemaining, "deduct");
    Object.assign(updated, afterAbove);

    if (remaining > 0) {
      updated[subId] = clampedValue - remaining;
    }
  } else if (diff < 0) {
    const { updated: afterBelow, remaining: afterBelowRemaining } = adjustEvenly(
      updated,
      belowSubs,
      Math.abs(diff),
      "add",
    );
    const { updated: afterAbove, remaining } = adjustEvenly(afterBelow, aboveSubs, afterBelowRemaining, "add");
    Object.assign(updated, afterAbove);

    if (remaining > 0) {
      updated[subId] = clampedValue + remaining;
    }
  }

  const totalForCluster = subIds.reduce((sum, id) => sum + (updated[id] || 0), 0);
  const discrepancy = clusterValue - totalForCluster;

  if (discrepancy !== 0) {
    const adjustBelowThenAbove = (
      adjustFn: typeof adjustEvenly,
      adjustAmount: number,
      mode: "add" | "deduct",
    ) => {
      const { updated: afterBelow, remaining: afterBelowRemaining } = adjustFn(updated, belowSubs, adjustAmount, mode);
      const { updated: afterAbove } = adjustFn(afterBelow, aboveSubs, afterBelowRemaining, mode);
      return afterAbove;
    };

    return discrepancy > 0
      ? adjustBelowThenAbove(adjustEvenly, discrepancy, "add")
      : adjustBelowThenAbove(adjustEvenly, Math.abs(discrepancy), "deduct");
  }

  return updated;
};
